import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  Sparkles,
  AlertCircle,
  Timer,
  TrendingUp,
} from "lucide-react";
import { timeEntriesApi } from "../../services/api";
import TrackTimeTab from "./TrackTimeTab";
import AiAssistantTab from "./AiAssistantTab";
import NeedsReviewTab from "./NeedsReviewTab";

type TabKey = "track" | "ai" | "review";

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "track", label: "Track Time", icon: <Clock className="h-4 w-4" /> },
  { key: "ai", label: "AI Assistant", icon: <Sparkles className="h-4 w-4" /> },
  { key: "review", label: "Needs Review", icon: <AlertCircle className="h-4 w-4" /> },
];

/** YYYY-MM-DD in the user's local time zone (toISOString would give the UTC date). */
const toLocalDateString = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const formatHours = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TimeTrackingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("track");
  const [summary, setSummary] = useState({
    hoursThisWeek: 0,
    totalUnbilled: 0,
    pendingReview: 0,
  });
  const [loadingSummary, setLoadingSummary] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      // Hours this week
      const now = new Date();
      const day = now.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() + mondayOffset);
      weekStart.setHours(0, 0, 0, 0);

      const [weekRes, unbilledRes, reviewRes] = await Promise.all([
        timeEntriesApi.list({
          dateFrom: toLocalDateString(weekStart),
          dateTo: toLocalDateString(now),
          limit: 500,
        }),
        timeEntriesApi.list({ status: "unbilled", limit: 500 }),
        timeEntriesApi.getReviewEntries(),
      ]);

      const weekMinutes = (weekRes.data?.data || []).reduce(
        (sum: number, e: any) => sum + (e.minutes || 0),
        0
      );
      const totalUnbilled = (unbilledRes.data?.data || []).reduce(
        (sum: number, e: any) => sum + (e.amountCents || 0),
        0
      );
      const pendingReview = Array.isArray(reviewRes.data) ? reviewRes.data.length : 0;

      setSummary({ hoursThisWeek: weekMinutes, totalUnbilled, pendingReview });
    } catch (err) {
      console.error("Failed to fetch time tracking summary:", err);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const summaryCards = [
    {
      label: "Hours This Week",
      value: formatHours(summary.hoursThisWeek),
      icon: <Timer className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Unbilled Time",
      value: formatCurrency(summary.totalUnbilled),
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      label: "Pending Review",
      value: String(summary.pendingReview),
      icon: <AlertCircle className="h-5 w-5" />,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Time Tracking</h1>
        <p className="text-sm text-slate-500 mt-1">
          Track time, review AI suggestions, and manage your timesheet
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                {loadingSummary ? (
                  <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
                )}
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="relative flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap"
              style={{
                color: activeTab === tab.key ? "var(--color-primary)" : "#64748b",
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.key === "review" && summary.pendingReview > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold text-white bg-purple-600 rounded-full">
                  {summary.pendingReview}
                </span>
              )}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="time-tracking-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === "track" && <TrackTimeTab onDataChange={fetchSummary} />}
        {activeTab === "ai" && <AiAssistantTab onDataChange={fetchSummary} />}
        {activeTab === "review" && <NeedsReviewTab onDataChange={fetchSummary} />}
      </motion.div>
    </div>
  );
};

export default TimeTrackingPage;
